import React, { useState } from 'react';

const titleCaseValue = (value = '') => String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

export const getReadinessResultFromAnswer = (answer = null) => {
    const result = answer?.readiness_result;
    if (!result || typeof result !== 'object') return null;
    const items = Array.isArray(result.items) ? result.items.filter(Boolean) : [];
    if (items.length === 0) return null;
    return {
        ...result,
        items,
    };
};

const ReadinessEvidenceReview = ({ result, onCitationOpen }) => {
    const [statusFilter, setStatusFilter] = useState('attention');
    const items = Array.isArray(result?.items) ? result.items : [];
    const counts = items.reduce((acc, item) => {
        const status = String(item?.status || 'missing');
        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});
    const visibleItems = items.filter((item) => {
        const status = String(item?.status || '');
        if (statusFilter === 'all') return true;
        if (statusFilter === 'attention') return status !== 'present';
        return status === statusFilter;
    });
    const selectedChecklists = Array.isArray(result?.selected_checklists) ? result.selected_checklists : [];

    return (
        <section className="library-rag-readiness-review" aria-label="Case readiness review">
            <div className="library-rag-readiness-review-header">
                <div>
                    <span className="library-rag-readiness-kicker">Readiness check</span>
                    <h3>{String(result?.case_name || 'Case readiness')}</h3>
                    <p>
                        {String(result?.checklist_case_type || result?.case_type || 'Case')} checklist
                        {selectedChecklists.length > 0 ? ` · ${selectedChecklists.length} selected` : ''}
                    </p>
                </div>
                <div className="library-rag-readiness-counts">
                    <span className="present">{counts.present || 0} present</span>
                    <span className="missing">{counts.missing || 0} missing</span>
                    <span className="unclear">{counts.unclear || 0} unclear</span>
                </div>
            </div>
            <div className="library-rag-readiness-controls">
                <button type="button" className={statusFilter === 'attention' ? 'active' : ''} onClick={() => setStatusFilter('attention')}>Needs review</button>
                <button type="button" className={statusFilter === 'missing' ? 'active' : ''} onClick={() => setStatusFilter('missing')}>Missing</button>
                <button type="button" className={statusFilter === 'unclear' ? 'active' : ''} onClick={() => setStatusFilter('unclear')}>Unclear</button>
                <button type="button" className={statusFilter === 'present' ? 'active' : ''} onClick={() => setStatusFilter('present')}>Present</button>
                <button type="button" className={statusFilter === 'all' ? 'active' : ''} onClick={() => setStatusFilter('all')}>All</button>
            </div>
            <div className="library-rag-readiness-list">
                {visibleItems.map((item, index) => {
                    const evidence = item?.evidence && typeof item.evidence === 'object' ? item.evidence : {};
                    const checkedSources = Array.isArray(evidence.checked_sources) ? evidence.checked_sources : [];
                    const matchedSources = Array.isArray(evidence.matched_sources) ? evidence.matched_sources : (Array.isArray(item?.sources) ? item.sources : []);
                    const nearMatches = Array.isArray(evidence.rejected_near_matches) ? evidence.rejected_near_matches : (Array.isArray(item?.near_matches) ? item.near_matches : []);
                    const sourceTags = checkedSources.flatMap((source) => [
                        ...(Array.isArray(source?.field_keys) ? source.field_keys : []),
                        ...(Array.isArray(source?.document_signals) ? source.document_signals : []),
                    ]).slice(0, 8);
                    return (
                        <article key={`readiness-item-${item?.item_id || index}`} className={`library-rag-readiness-item ${String(item?.status || 'missing')}`}>
                            <div className="library-rag-readiness-item-topline">
                                <div>
                                    <span className="library-rag-readiness-item-kind">{titleCaseValue(item?.kind || 'item')}</span>
                                    <h4>{String(item?.label || 'Readiness item')}</h4>
                                </div>
                                <span className={`library-rag-readiness-status ${String(item?.status || 'missing')}`}>{String(item?.status || 'missing')}</span>
                            </div>
                            {item?.notes ? <p>{String(item.notes)}</p> : null}
                            {evidence?.reason ? <p className="library-rag-readiness-reason">{String(evidence.reason)}</p> : null}
                            <div className="library-rag-readiness-evidence-row">
                                <span>Confidence: {String(item?.confidence || evidence?.confidence || 'unknown').replace(/_/g, ' ')}</span>
                                {checkedSources[0]?.available_field_count ? <span>{checkedSources[0].available_field_count} fields checked</span> : null}
                                {checkedSources[0]?.available_document_count ? <span>{checkedSources[0].available_document_count} documents checked</span> : null}
                            </div>
                            {sourceTags.length > 0 ? (
                                <div className="library-rag-readiness-tags">
                                    {sourceTags.map((tag, tagIndex) => <span key={`readiness-tag-${index}-${tagIndex}`}>{String(tag)}</span>)}
                                </div>
                            ) : null}
                            {matchedSources.length > 0 ? (
                                <div className="library-rag-readiness-sources">
                                    {matchedSources.slice(0, 5).map((source, sourceIndex) => (
                                        <button
                                            type="button"
                                            key={`readiness-source-${index}-${sourceIndex}`}
                                            onClick={() => onCitationOpen?.(source)}
                                        >
                                            [{source?.citation_number || source?.number || sourceIndex + 1}] {String(source?.label || source?.file_name || 'Source')}
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                            {nearMatches.length > 0 ? (
                                <details className="library-rag-readiness-near-matches">
                                    <summary>{nearMatches.length} lower-confidence near match{nearMatches.length === 1 ? '' : 'es'}</summary>
                                    {nearMatches.slice(0, 5).map((match, matchIndex) => (
                                        <div key={`readiness-near-${index}-${matchIndex}`}>
                                            <strong>{String(match?.field_name || match?.file_name || 'Near match')}</strong>
                                            <span>{match?.score ? `Score ${match.score}` : 'Lower confidence'}</span>
                                        </div>
                                    ))}
                                </details>
                            ) : null}
                            {item?.suggested_next_step ? <div className="library-rag-readiness-next-step">{String(item.suggested_next_step)}</div> : null}
                        </article>
                    );
                })}
            </div>
        </section>
    );
};

export default ReadinessEvidenceReview;
